import apiClient from './api';
import type { FieldStats, Geometry, LayerStyle } from '@/utils/layerStyle';

/**
 * How a layer looks to the person looking (geonexora-server styles/LayerStyleApi): its main look, which everyone sees,
 * and the person's own view, which only they see. Anyone who sees a layer may keep a view; the main look is changed by
 * those allowed - a company layer's through the workspace (PUT /workspaces/{ws}/layers/{key}), one of ours by the
 * platform's staff (PUT /admin/layers/{key}).
 *
 * A layer is `company/<key>` or `platform/<workspace:name>`.
 */

export type LayerKind = 'company' | 'platform';

/** A style, the field written beside each feature, and the scales the layer is drawn between. */
export interface LayerLook {
  style?: LayerStyle | null;
  label?: string | null;
  minScale?: number | null;
  maxScale?: number | null;
}

export interface StyleField {
  key: string;
  column: string;
  type: string;
  labels: Record<string, string>;
  hidden?: boolean;
}

export interface LayerStyleInfo {
  kind: LayerKind;
  ref: string;
  /** What the main look is saved under: the company layer's key, or our layer's map-layer directive. */
  key: string;
  workspace?: string | null;
  labels: Record<string, string>;
  geometry: Geometry;
  modes: string[];
  clusters: boolean;
  main: LayerLook;
  /** False for one of ours drawn by a style GeoServer keeps or a hand-written SLD: the editor starts afresh. */
  mainEditable: boolean;
  mine: (LayerLook & { updatedAt?: string }) | null;
  canKeepView: boolean;
  canEditMain: boolean;
  fields: StyleField[];
}

export interface MyViews {
  canEditPlatformMain: boolean;
  canKeepViews: boolean;
  views: { kind: LayerKind; ref: string; look: LayerLook; updatedAt: string }[];
}

const BASE = '/v2/layer-styles';
const at = (kind: LayerKind, ref: string) => `${BASE}/${kind}/${encodeURIComponent(ref)}`;

export const layerStylesService = {
  async mine(): Promise<MyViews> {
    return (await apiClient.get<MyViews>(BASE)).data;
  },
  async info(kind: LayerKind, ref: string): Promise<LayerStyleInfo> {
    return (await apiClient.get<LayerStyleInfo>(at(kind, ref))).data;
  },
  async keep(kind: LayerKind, ref: string, look: LayerLook): Promise<LayerStyleInfo> {
    return (await apiClient.put<LayerStyleInfo>(`${at(kind, ref)}/mine`, look)).data;
  },
  async reset(kind: LayerKind, ref: string): Promise<void> {
    await apiClient.delete(`${at(kind, ref)}/mine`);
  },
  /** A token for GeoServer to draw a look of one of ours before it is saved (the map's GXPREVIEW parameter). */
  async preview(ref: string, look: LayerLook): Promise<string> {
    return (await apiClient.post<{ token: string }>(`${at('platform', ref)}/preview`, look)).data.token;
  },
  async fieldStats(kind: LayerKind, ref: string, field: string): Promise<FieldStats> {
    return (await apiClient.get<FieldStats>(`${at(kind, ref)}/fields/${encodeURIComponent(field)}/stats`)).data;
  },
};

export default layerStylesService;
